import { FormEvent, useEffect, useMemo, useState } from 'react'

import * as api from 'api/api'
import { Material, MaterialInput } from 'api/types'
import { useAuth } from 'auth/AuthContext'
import { useConfirm } from 'components/Confirm'
import { Icon } from 'components/Icon'
import { Button, EmptyState, ErrorState, IconButton, Sheet, Spinner, TextAreaField, TextField } from 'components/ui'
import { formatNumber } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'
import { ForbiddenPage } from 'pages/system/ForbiddenPage'

const EMPTY_FORM: MaterialInput = {
  category: '',
  defaultSupplier: '',
  name: '',
  notes: '',
  unit: '',
}

const MaterialForm = ({
  initial,
  busy,
  onCancel,
  onSubmit,
}: {
  initial?: Material
  busy: boolean
  onCancel: () => void
  onSubmit: (input: MaterialInput) => void
}) => {
  const [form, setForm] = useState<MaterialInput>(() =>
    initial
      ? {
          category: initial.category,
          defaultSupplier: initial.defaultSupplier ?? '',
          name: initial.name,
          notes: initial.notes ?? '',
          unit: initial.unit,
        }
      : EMPTY_FORM
  )

  const valid = form.name.trim() && form.unit.trim() && form.category.trim()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!valid) return
    onSubmit({
      category: form.category.trim(),
      defaultSupplier: form.defaultSupplier?.trim() || null,
      name: form.name.trim(),
      notes: form.notes?.trim() || null,
      unit: form.unit.trim(),
    })
  }

  return (
    <Sheet
      title={initial ? t('تعديل مادة', 'Edit material') : t('إضافة مادة', 'Add material')}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('إلغاء', 'Cancel')}</Button>
          <Button type="submit" form="material-form" variant="primary" busy={busy} disabled={!valid}>
            {initial ? t('حفظ', 'Save') : t('إضافة', 'Add')}
          </Button>
        </>
      }
    >
      <form id="material-form" className="form-grid" onSubmit={submit}>
        <TextField
          label={t('اسم المادة', 'Material name')}
          className="span-2"
          value={form.name}
          onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
          placeholder={t('مثال: حديد تسليح Ø16', 'Example: Ø16 rebar')}
          autoFocus
          required
        />
        <TextField
          label={t('وحدة القياس', 'Unit')}
          value={form.unit}
          onChange={(event) => setForm((current) => ({ ...current, unit: event.target.value }))}
          placeholder={t('مثال: طن', 'Example: tonne')}
          required
        />
        <TextField
          label={t('التصنيف', 'Category')}
          value={form.category}
          onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
          placeholder={t('مثال: حديد', 'Example: Steel')}
          required
        />
        <TextField
          label={t('المورّد الافتراضي', 'Default supplier')}
          className="span-2"
          value={form.defaultSupplier ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, defaultSupplier: event.target.value }))}
          placeholder={t('مثال: الأردنية للحديد', 'Example: Jordan Steel')}
        />
        <TextAreaField
          label={t('ملاحظات', 'Notes')}
          className="span-2"
          rows={3}
          maxLength={500}
          hint={t('مواصفات أو تعليمات تخزين (اختياري)', 'Specification or storage notes (optional)')}
          value={form.notes ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
        />
      </form>
    </Sheet>
  )
}

/** Global materials reference registry — admin console, portfolio-wide (Figma: Admin / 07 · Materials, 08 · Add material).
 *  Distinct from the per-project material-name catalogue (still used when filling in a report's materials section).
 *  "Used this month" is computed from approved reports' material lines across every project this month, matching
 *  the Figma hint ("Usage totals come from approved reports") — it is not a manually-entered figure. */
export const MaterialsRegistryPage = () => {
  const { user } = useAuth()
  const { data: materials, error, loading, reload, setData } = useAsync(api.fetchMaterials, [])
  const { busy, run } = useAction()
  const confirm = useConfirm()

  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(new Set())
  const [supplierFilter, setSupplierFilter] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Material | null>(null)

  const categoryOptions = useMemo(() => Array.from(new Set((materials ?? []).map((item) => item.category))).sort(), [materials])
  const supplierOptions = useMemo(
    () =>
      Array.from(new Set((materials ?? []).map((item) => item.defaultSupplier).filter((value): value is string => Boolean(value)))).sort(),
    [materials]
  )

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (materials ?? []).filter((item) => {
      if (categoryFilter.size && !categoryFilter.has(item.category)) return false
      if (supplierFilter.size && !supplierFilter.has(item.defaultSupplier ?? '')) return false
      if (term && !item.code.toLowerCase().includes(term) && !item.name.toLowerCase().includes(term)) return false
      return true
    })
  }, [materials, search, categoryFilter, supplierFilter])

  useEffect(() => {
    setPage(1)
  }, [search, categoryFilter, supplierFilter, pageSize])

  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize))
  const safePage = Math.min(page, pageCount)
  const pageItems = visible.slice((safePage - 1) * pageSize, safePage * pageSize)
  const pageStart = visible.length ? (safePage - 1) * pageSize + 1 : 0
  const pageEnd = Math.min(safePage * pageSize, visible.length)

  if (!user?.isAdmin) return <ForbiddenPage message={t('سجل المواد متاح لمدير النظام فقط.', 'The materials registry is available to admins only.')} />

  const toggle = <T,>(set: Set<T>, setter: (next: Set<T>) => void, value: T) => {
    const next = new Set(set)
    if (next.has(value)) next.delete(value)
    else next.add(value)
    setter(next)
  }

  const create = async (input: MaterialInput) => {
    const created = await run('add', () => api.createMaterial(input), t('تمت إضافة المادة', 'Material added'))
    if (created) {
      setData((current) => [...(current ?? []), created])
      setAdding(false)
    }
  }

  const update = async (input: MaterialInput) => {
    if (!editing) return
    const updated = await run(`edit${editing.id}`, () => api.updateMaterial(editing.id, input), t('تم حفظ التعديلات', 'Changes saved'))
    if (updated) {
      setData((current) => current?.map((item) => (item.id === updated.id ? updated : item)))
      setEditing(null)
    }
  }

  const remove = async (item: Material) => {
    if (!(await confirm(t(`حذف «${item.name}» (${item.code}) من سجل المواد؟`, `Delete "${item.name}" (${item.code}) from the materials registry?`), { confirmLabel: t('حذف', 'Delete') }))) return
    const done = await run(`rm${item.id}`, () => api.removeMaterial(item.id), t('تم الحذف', 'Deleted'))
    if (done) setData((current) => current?.filter((entry) => entry.id !== item.id))
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('المواد', 'Materials')}</h1>
          <p className="muted">
            {materials ? t(`${materials.length} مادة · ${categoryOptions.length} تصنيف · ${supplierOptions.length} مورّد`, `${materials.length} materials · ${categoryOptions.length} categories · ${supplierOptions.length} suppliers`) : ''}
          </p>
        </div>
        <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
          {t('إضافة مادة', 'Add material')}
        </Button>
      </div>

      <div className="toolbar">
        <div className="search">
          <Icon name="search" size={16} />
          <label htmlFor="material-search" className="sr-only">
            {t('بحث', 'Search')}
          </label>
          <input
            id="material-search"
            type="search"
            placeholder={t('بحث بالرقم أو الاسم...', 'Search by number or name...')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {categoryOptions.length ? (
          <details className="dropdown">
            <summary className="btn">{t('التصنيف', 'Category')}{categoryFilter.size ? ` (${categoryFilter.size})` : ''}</summary>
            <div className="dropdown__panel">
              {categoryOptions.map((category) => (
                <label key={category} className="dropdown__item">
                  <input
                    type="checkbox"
                    checked={categoryFilter.has(category)}
                    onChange={() => toggle(categoryFilter, setCategoryFilter, category)}
                  />
                  {category}
                </label>
              ))}
            </div>
          </details>
        ) : null}
        {supplierOptions.length ? (
          <details className="dropdown">
            <summary className="btn">{t('المورّد الافتراضي', 'Default supplier')}{supplierFilter.size ? ` (${supplierFilter.size})` : ''}</summary>
            <div className="dropdown__panel">
              {supplierOptions.map((supplier) => (
                <label key={supplier} className="dropdown__item">
                  <input
                    type="checkbox"
                    checked={supplierFilter.has(supplier)}
                    onChange={() => toggle(supplierFilter, setSupplierFilter, supplier)}
                  />
                  {supplier}
                </label>
              ))}
            </div>
          </details>
        ) : null}
      </div>

      {loading && !materials ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !materials?.length ? (
        <EmptyState title={t('لا توجد مواد مسجّلة بعد', 'No materials recorded yet')} icon="box">
          <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
            {t('إضافة مادة', 'Add material')}
          </Button>
        </EmptyState>
      ) : !visible.length ? (
        <EmptyState title={t('لا توجد نتائج مطابقة', 'No matching results')} icon="search" />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('الرقم', 'Number')}</th>
                <th scope="col">{t('المادة', 'Material')}</th>
                <th scope="col">{t('الوحدة', 'Unit')}</th>
                <th scope="col">{t('التصنيف', 'Category')}</th>
                <th scope="col">{t('المورّد الافتراضي', 'Default supplier')}</th>
                <th scope="col">{t('الاستخدام هذا الشهر', 'Used this month')}</th>
                <th scope="col">
                  <span className="sr-only">{t('إجراءات', 'Actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((item) => (
                <tr key={item.id}>
                  <td className="code-link">{item.code}</td>
                  <td>
                    <strong>{item.name}</strong>
                  </td>
                  <td>{item.unit}</td>
                  <td>{item.category}</td>
                  <td>{item.defaultSupplier ?? <span className="muted">—</span>}</td>
                  <td>
                    {item.usedThisMonth != null ? (
                      `${formatNumber(item.usedThisMonth)} ${item.usedThisMonthUnit ?? item.unit}`
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <IconButton icon="edit" label={t(`تعديل ${item.name}`, `Edit ${item.name}`)} onClick={() => setEditing(item)} />
                      <IconButton
                        icon="trash"
                        label={t(`حذف ${item.name}`, `Delete ${item.name}`)}
                        onClick={() => remove(item)}
                        disabled={busy === `rm${item.id}`}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-footer">
            <span className="muted small">{t(`${visible.length} مادة`, `${visible.length} materials`)}</span>
            <span className="spacer" />
            <label className="table-footer__rows">
              {t('صفوف لكل صفحة', 'Rows per page')}
              <select className="input" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </label>
            <span className="muted small">
              {pageStart}–{pageEnd} {t('من', 'of')} {visible.length}
            </span>
            <div className="table-footer__pagination">
              <IconButton icon="chevronsRight" label={t('الصفحة الأولى', 'First page')} disabled={safePage <= 1} onClick={() => setPage(1)} />
              <IconButton
                icon="chevronRight"
                label={t('الصفحة السابقة', 'Previous page')}
                disabled={safePage <= 1}
                onClick={() => setPage(Math.max(1, safePage - 1))}
              />
              <IconButton
                icon="chevronLeft"
                label={t('الصفحة التالية', 'Next page')}
                disabled={safePage >= pageCount}
                onClick={() => setPage(Math.min(pageCount, safePage + 1))}
              />
              <IconButton icon="chevronsLeft" label={t('الصفحة الأخيرة', 'Last page')} disabled={safePage >= pageCount} onClick={() => setPage(pageCount)} />
            </div>
          </div>
        </div>
      )}

      {adding ? <MaterialForm busy={busy === 'add'} onCancel={() => setAdding(false)} onSubmit={create} /> : null}
      {editing ? (
        <MaterialForm initial={editing} busy={busy === `edit${editing.id}`} onCancel={() => setEditing(null)} onSubmit={update} />
      ) : null}
    </div>
  )
}
